import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { ApiError } from '../api/api-error';
import { PAYMENT_METHOD_LABELS } from '../lib/format';
import { AMOUNT_PATTERN, createPendingPaymentStore, getDefaultLocalStorage } from '../lib/pending-payment';
import { adminApi as defaultAdminApi } from './admin-api';
import { PendingPaymentBanner } from './PendingPaymentBanner';
import { useFocusTrap } from '../components/useFocusTrap';

const METHOD_OPTIONS = Object.entries(PAYMENT_METHOD_LABELS);
const UUID_FAILURE_MESSAGE = 'No se pudo generar un identificador seguro para este cobro en este navegador. Probá de nuevo, o usá un navegador/contexto que soporte crypto.randomUUID (por ejemplo, HTTPS).';
const STORAGE_FAILURE_MESSAGE = 'No se pudo registrar el intento de forma segura. No se envió el cobro.';

/**
 * Classifies a `POST /payments` failure into the exact outcome the plan's
 * Step 3 table defines. Anything not explicitly listed as `rejected` or
 * `conflict` — 401, 403, network_error (status 0), a 5xx, an unparsable body,
 * or any other unlisted status/code combination — is `uncertain` on purpose:
 * money-critical means a code this function doesn't recognize must be
 * treated as "don't know", never assumed safe to clear.
 */
function outcomeFor(error) {
  if (error instanceof ApiError) {
    const { status, code } = error;
    if (
      (status === 400 && code === 'validation_error')
      || (status === 404 && code === 'membership_not_found')
      || (status === 409 && code === 'payment_exceeds_balance')
      || (status === 413 && code === 'bad_request')
    ) {
      return 'rejected';
    }
    if (status === 409 && code === 'idempotency_conflict') return 'conflict';
  }
  return 'uncertain';
}

/**
 * "Cobrar" action inside a member's period row (spec "Administrador", cobro
 * paragraph; plan Task 10). One UUID v4 is generated per attempt and, together
 * with its full signature (`membershipId`, `amount`, `date`, `method`), is
 * persisted via `createPendingPaymentStore` BEFORE the `POST /payments` call
 * — never after — so a response lost to a network drop, a reload, or a 401
 * mid-flight leaves a durable record the admin can only resolve by retrying
 * that exact payload, never by opening a second, different charge.
 *
 * Only a definitive 200 (replay) or 201 (created) clears the record; every
 * other outcome either clears it because the server confirms it never
 * happened (`rejected`) or keeps it because the outcome is still unknown or
 * blocked (`uncertain`/`conflict` — see `outcomeFor`). A successful charge
 * always calls `onResolved` (MemberDetailPage's `reload()`) instead of
 * adjusting `membership.balance`/`paid` locally — the spec is explicit that
 * the client never recomputes balances.
 *
 * `storage` and `uuid` are injectable (default the real, guarded
 * `window.localStorage` / `crypto.randomUUID`) purely for deterministic
 * tests; production always gets the real browser storage and a real random
 * UUID. `MemberDetailPage` passes its OWN `storage` explicitly (not just this
 * default) so the banner it renders when balance is already 0 reads the
 * exact same source this component writes to (fix round 1, item 1).
 */
export function PaymentForm({
  api = defaultAdminApi,
  membership,
  actorId,
  storage = getDefaultLocalStorage(),
  uuid = () => crypto.randomUUID(),
  onResolved = () => {},
}) {
  const { handleApiError } = useAuth();
  const store = useState(() => createPendingPaymentStore(storage))[0];

  const [pending, setPending] = useState(() => store.forMembership(membership.id));
  const [phase, setPhase] = useState(pending ? 'uncertain' : 'idle');
  const [formOpen, setFormOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [method, setMethod] = useState('cash');
  const [error, setError] = useState('');
  const sheetRef = useFocusTrap(formOpen);

  const submitting = phase === 'submitting';

  function resetFields() {
    setAmount('');
    setDate('');
    setMethod('cash');
  }

  // Shared by a fresh submit (after `store.begin`) and a retry (reusing the
  // stored payload/key untouched): the only difference between the two is
  // where `payload`/`idempotencyKey` came from, never how the result is
  // interpreted.
  async function performRequest(payload, idempotencyKey) {
    setPhase('submitting');
    setError('');
    try {
      await api.recordPayment({ ...payload, idempotencyKey });
      store.resolve(idempotencyKey);
      setPending(null);
      setPhase('idle');
      setFormOpen(false);
      resetFields();
      await onResolved();
    } catch (caught) {
      const outcome = outcomeFor(caught);
      if (outcome === 'rejected') {
        // The server confirms this exact attempt never took effect: nothing
        // to retry, so the slot opens up again for a corrected new attempt.
        // Fix round 1, item 6: this can be reached from the BANNER's retry
        // (not just a fresh submit), where `formOpen` was never set to
        // `true` — the error is rendered above regardless of `formOpen`/
        // `pending`, so it's never silently dropped when the view collapses
        // back to the idle "Cobrar" button.
        store.resolve(idempotencyKey);
        setPending(null);
        setPhase('idle');
        setError(errorMessageFor(caught));
        return;
      }
      if (outcome === 'conflict') {
        setPending(store.forMembership(payload.membershipId));
        setPhase('conflict');
        setError(errorMessageFor(caught));
        return;
      }
      // uncertain: 401/403/network/timeout/invalid JSON/5xx/anything else.
      // handleApiError's session-clearing side effect (401/403) is orthogonal
      // to the pending record, which must survive it regardless.
      const globalOutcome = handleApiError(caught);
      setPending(store.forMembership(payload.membershipId));
      setPhase('uncertain');
      if (!globalOutcome.handled) setError(errorMessageFor(caught));
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    setError('');

    const trimmedAmount = amount.trim();
    // Fix round 1, item 2: validated against the EXACT format the server
    // accepts for a string amount (`AMOUNT_PATTERN`, shared with
    // `pending-payment.js`'s own canonicalization) instead of a bare
    // `Number(...)`, which also parses hex/exponential/leading-dot notation
    // no real amount input ever produces.
    if (!AMOUNT_PATTERN.test(trimmedAmount)) {
      setError('Ingresá un importe válido (por ejemplo 9000 o 9000.50).');
      return;
    }
    const numericAmount = Number(trimmedAmount);
    if (numericAmount <= 0) {
      setError('Ingresá un importe válido.');
      return;
    }
    // UI-only guardrail (spec: prevented in the UI without replacing the
    // server's own check) — the server still re-validates against its own,
    // possibly more recent, balance snapshot under `FOR UPDATE`.
    if (numericAmount > membership.balance) {
      setError('El importe no puede superar el saldo pendiente del período.');
      return;
    }
    if (!date) {
      setError('Ingresá la fecha del pago.');
      return;
    }

    // `amount` travels as the exact (now format-validated) string the admin
    // typed, never a parsed Number. `pending-payment.js` canonicalizes it
    // internally before comparing signatures, so a retry rebuilt from a
    // differently-formatted-but-equal amount (e.g. a number vs. this string)
    // still matches — this component itself never needs to reproduce a byte-
    // for-byte identical string, only reuse `record.payload` (the store's own
    // canonical copy) for every retry, which it already does below.
    const payload = {
      membershipId: membership.id, amount: trimmedAmount, date, method,
    };

    let idempotencyKey;
    try {
      // Fix round 1, item 4: `crypto.randomUUID` doesn't exist outside a
      // secure context (plain HTTP, some embedded webviews) — call it inside
      // its own try/catch so that failure surfaces as a normal, visible
      // error instead of an unhandled rejection from this `async` submit
      // handler.
      idempotencyKey = uuid();
    } catch {
      setError(UUID_FAILURE_MESSAGE);
      return;
    }

    let record;
    try {
      record = store.begin({ actorId, payload, idempotencyKey });
    } catch {
      // `begin` throws for two different reasons and they need different UI:
      // (1) a pending charge for THIS membership already exists (another tab,
      // or this admin already started one) — surface it as the banner. (2) a
      // storage failure (quota, private mode, `SecurityError` reading
      // storage) or a freak UUID collision with another membership — nothing
      // was persisted, so the charge must NOT be sent (fail closed); show a
      // blocking error and keep the form open instead of pretending the
      // attempt is safely tracked.
      const existing = store.forMembership(membership.id);
      if (existing) {
        setPending(existing);
        setPhase('uncertain');
      } else {
        setError(STORAGE_FAILURE_MESSAGE);
      }
      return;
    }

    await performRequest(record.payload, record.idempotencyKey);
  }

  function handleRetry() {
    if (!pending || submitting) return;
    performRequest(pending.payload, pending.idempotencyKey);
  }

  function closeForm() {
    setFormOpen(false);
    setError('');
    resetFields();
  }

  if (pending) {
    return (
      <PendingPaymentBanner
        record={pending}
        actorId={actorId}
        phase={phase}
        error={error}
        retrying={submitting}
        onRetry={handleRetry}
      />
    );
  }

  return (
    <div className="gym-admin-payment">
      <button type="button" tabIndex={formOpen ? -1 : undefined} className="gym-button gym-button--secondary" onClick={() => setFormOpen(true)}>
        Cobrar
      </button>
      {!formOpen && error && <p role="alert" className="gym-form__error">{error}</p>}

      {formOpen ? (
        <div className="gym-admin-detail-sheet-backdrop">
          <form
            ref={sheetRef}
            tabIndex={-1}
            className="gym-form gym-admin-payment-form gym-admin-detail-sheet"
            onSubmit={handleSubmit}
            noValidate
            aria-label={`Registrar cobro — ${membership.planName}`}
          >
          <div className="gym-admin-detail-sheet__header">
            <h2>Registrar cobro — {membership.planName}</h2>
            <button type="button" className="gym-admin-detail-sheet__close" aria-label="Cerrar" disabled={submitting} onClick={closeForm}>×</button>
          </div>
          {error && <p role="alert" className="gym-form__error">{error}</p>}
          <div className="gym-field">
            <label htmlFor={`payment-amount-${membership.id}`} className="gym-field__label">Importe</label>
            <input
              id={`payment-amount-${membership.id}`}
              name="amount"
              type="text"
              inputMode="decimal"
              required
              value={amount}
              disabled={submitting}
              onChange={(event) => setAmount(event.target.value)}
              className="gym-input"
            />
          </div>

          <div className="gym-field">
            <label htmlFor={`payment-date-${membership.id}`} className="gym-field__label">Fecha</label>
            <input
              id={`payment-date-${membership.id}`}
              name="date"
              type="date"
              required
              value={date}
              disabled={submitting}
              onChange={(event) => setDate(event.target.value)}
              className="gym-input"
            />
          </div>

          <fieldset className="gym-admin-payment-methods" disabled={submitting}>
            <legend className="gym-field__label">Método</legend>
            <div className="gym-admin-payment-methods__options">
              {METHOD_OPTIONS.map(([value, label]) => (
                <label key={value} className={`gym-admin-payment-method${method === value ? ' gym-admin-payment-method--selected' : ''}`}>
                  <input type="radio" name="method" value={value} checked={method === value} onChange={() => setMethod(value)} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="gym-admin-form-actions">
            <button
              type="button"
              className="gym-button gym-button--secondary"
              disabled={submitting}
              onClick={closeForm}
            >
              Cancelar
            </button>
            <button type="submit" disabled={submitting} className="gym-button gym-button--primary">
              {submitting ? 'Registrando…' : 'Registrar cobro'}
            </button>
          </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
